import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { EditIcon, XCloseIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { Tooltip } from '../../../components/tooltip/index';
import {
  MAX_ACTIVE_SHARED_CAP_TABLE_LINKS,
  SharedCapTableLink,
} from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { useLocale } from '../../../hooks/use-locale.hook';
import { isDefaultSharedView } from './share-default-view.util';
import { buildSharedCapTableUrl } from './share-link-url.util';

type ShareLinksManageModalProps = {
  companyName: string;
  isPending: boolean;
  links: SharedCapTableLink[];
  onCancel: () => void;
  onCreate: () => void;
  onDelete: (link: SharedCapTableLink) => void;
  onEdit: (link: SharedCapTableLink) => void;
};

const formatDate = (value: string) => new Date(value).toLocaleDateString('en-GB');

const MetaDot: FC = () => <span className="size-1 shrink-0 rounded-full bg-gray-400" />;

export const ShareLinksManageModal: FC<ShareLinksManageModalProps> = ({
  companyName,
  isPending,
  links,
  onCancel,
  onCreate,
  onDelete,
  onEdit,
}) => {
  const { messagesLocale } = useLocale();
  const isLimitReached = links.length >= MAX_ACTIVE_SHARED_CAP_TABLE_LINKS;
  // The list arrives newest-first so a bounded read keeps the newest links; the numbering counts
  // from the oldest, so display order is reversed here rather than in the query.
  const orderedLinks = [...links].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
  );
  const listRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  // The fade is a cue that the list runs on under the footer, so it has to answer to the scroll
  // position: left on at the end of the list it reads as a permanently half-hidden last row.
  const syncScrollCue = useCallback(() => {
    const list = listRef.current;

    // Unmounted with the last link, and a cue left on would blur the empty-state message.
    setHasMoreBelow(!!list && list.scrollHeight - list.scrollTop - list.clientHeight > 1);
  }, []);

  useEffect(syncScrollCue, [links.length, syncScrollCue]);

  const handleCopy = async (link: SharedCapTableLink) => {
    try {
      await navigator.clipboard.writeText(buildSharedCapTableUrl(companyName, link.linkId));
      toast(messagesLocale['SHARE_LINK_COPIED']);
    } catch {
      toast(messagesLocale['SHARE_LINK_COPY_FAILED']);
    }
  };

  return (
    // 493×762 with 16px padding, and the same fixed-height shell as the share modal: the footer
    // holds still while the list scrolls under it.
    <div className="flex h-[90vh] max-h-[90vh] w-full max-w-[493px] flex-col rounded bg-white p-4 xs:h-[762px]">
      <div className="flex shrink-0 items-center justify-between gap-2 py-1">
        <span className="text-xl font-semibold leading-8 text-gray-700">
          <AppFormattedMessage id="SHARE_ACTIVE_LINKS" values={{ count: links.length }} />
        </span>
      </div>

      <div className="relative mt-4 min-h-0 flex-1">
        {/* Either the list or the message, never both: two `h-full` children in the same column
            doubled this block's height and pushed the footer buttons out of the modal. */}
        {orderedLinks.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center">
            <span className="text-sm font-[450] text-gray-400">
              <AppFormattedMessage id="SHARE_NO_ACTIVE_LINKS" />
            </span>
          </div>
        ) : (
          <div
            className="flex h-full flex-col gap-4 overflow-y-auto"
            onScroll={syncScrollCue}
            ref={listRef}
          >
            {orderedLinks.map((link, index) => (
              <div
                className="flex shrink-0 flex-col gap-2 rounded-md bg-gray-50 px-1 pb-1 pt-2"
                key={link.id}
              >
                <div className="flex flex-col gap-0.5 px-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-[550] leading-5 text-gray-700">
                      <AppFormattedMessage id="SHARE_LINK_NUMBER" values={{ number: index + 1 }} />
                    </span>
                    <span className="text-label-sm font-medium text-gray-400">
                      {formatDate(link.createdAt)}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-label-sm font-medium text-gray-400">
                    <span>
                      {link.expiresAt ? (
                        <AppFormattedMessage
                          id="SHARE_EXPIRES_ON"
                          values={{ date: formatDate(link.expiresAt) }}
                        />
                      ) : (
                        <AppFormattedMessage id="SHARE_NO_EXPIRY" />
                      )}
                    </span>
                    {link.hasPassword && (
                      <>
                        <MetaDot />
                        <span>
                          <AppFormattedMessage id="SHARE_PASSWORD_PROTECTED" />
                        </span>
                      </>
                    )}
                    {link.settings.viewOptions.hideStakeholderNames && (
                      <>
                        <MetaDot />
                        <span>
                          <AppFormattedMessage id="SHARE_NAMES_HIDDEN" />
                        </span>
                      </>
                    )}
                    <MetaDot />
                    <span>
                      <AppFormattedMessage
                        id={
                          isDefaultSharedView(link.settings)
                            ? 'SHARE_DEFAULT_VIEW'
                            : 'SHARE_CUSTOM_VIEW'
                        }
                      />
                    </span>
                    {link.note && (
                      <>
                        <MetaDot />
                        {/* A popover rather than the hover tooltip: the note is opened deliberately,
                            and it has to stay put while it is read. */}
                        <Popover>
                          <PopoverTrigger asChild>
                            {/* Reads as the rest of the meta line until it is the one open. */}
                            <button
                              className="text-label-sm font-medium text-gray-400 data-[state=open]:text-gray-600 data-[state=open]:underline"
                              type="button"
                            >
                              <AppFormattedMessage id="SHARE_VIEW_NOTE" />
                            </button>
                          </PopoverTrigger>

                          {/* Portalled beside the dialog, whose own layer is `z-[61]`, so the
                              default `z-50` put the note behind the modal. Focus stays on the
                              trigger: the dialog's focus trap would pull it straight back and
                              close the popover in the same tick. */}
                          <PopoverContent
                            align="center"
                            className="z-[62] flex w-auto max-w-[312px] flex-col gap-1 whitespace-normal rounded-lg border-none bg-gray-900 p-3 shadow-2xl"
                            onOpenAutoFocus={(event) => event.preventDefault()}
                            side="top"
                          >
                            <span className="text-sm font-[550] text-white">
                              <AppFormattedMessage id="SHARE_CUSTOM_NOTE" />
                            </span>
                            {/* Company-authored text rendered as text — never as markup. */}
                            <span className="whitespace-pre-wrap text-xs font-normal leading-[18px] text-white">
                              {link.note}
                            </span>
                            <PopoverArrow />
                          </PopoverContent>
                        </Popover>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-6 rounded-md bg-white p-3 shadow-xs">
                    <span className="flex-1 truncate text-xs font-[450] text-gray-700">
                      {buildSharedCapTableUrl(companyName, link.linkId)}
                    </span>
                    <div className="flex shrink-0 items-center gap-[7px]">
                      <Button
                        className="h-8 w-fit rounded border border-gray-100 bg-white px-3 text-xs font-[450] text-gray-700 shadow-xs"
                        onClick={() => void handleCopy(link)}
                        styleType="NONE"
                      >
                        <AppFormattedMessage id="SHARE_COPY" />
                      </Button>
                      <Button
                        aria-label={messagesLocale['EDIT']}
                        className="flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-100 bg-white p-0 shadow-xs"
                        onClick={() => onEdit(link)}
                        styleType="NONE"
                      >
                        <EditIcon iconColor="#344054" />
                      </Button>
                      <Button
                        aria-label={messagesLocale['DELETE']}
                        className="flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-100 bg-white p-0 shadow-xs"
                        disabled={isPending}
                        onClick={() => onDelete(link)}
                        styleType="NONE"
                      >
                        <XCloseIcon iconColor="#344054" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          className={twMerge(
            'pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-white/30 backdrop-blur-[5px] transition-opacity duration-200',
            hasMoreBelow ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>

      <div className="flex shrink-0 items-center justify-between gap-4 pt-4">
        <Button
          className="h-11 w-fit rounded border border-gray-300 px-6 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        {/* The tooltip is the only place the limit is explained, so it must stay reachable on a
            disabled button — hence the wrapper rather than a title on the button itself. */}
        <Tooltip
          className="max-w-[312px] rounded-lg p-3 shadow-2xl"
          content={
            <span className="flex flex-col gap-1 text-left">
              <span className="text-sm font-[550] text-white">
                <AppFormattedMessage id="SHARE_LIMIT_REACHED_TITLE" />
              </span>
              <span className="text-xs font-normal leading-[18px] text-white">
                <AppFormattedMessage
                  id="SHARE_LIMIT_REACHED"
                  values={{ count: MAX_ACTIVE_SHARED_CAP_TABLE_LINKS }}
                />
              </span>
              <a
                className="mt-3 w-fit text-xs font-medium text-white underline"
                href="mailto:contact@capquest.io"
              >
                <AppFormattedMessage id="CONTACT_SUPPORT" />
              </a>
            </span>
          }
          disabled={!isLimitReached}
          placement="top"
        >
          <Button
            className="h-11 w-fit rounded px-6 text-sm font-[550] text-white"
            disabled={isLimitReached}
            onClick={onCreate}
          >
            <AppFormattedMessage id="SHARE_CREATE_NEW_LINK" />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
